// La buvette, côté logique (D-152, D-182, D-184) : les montants, le panier, l'alcool et ses autorisations, le comptage du
// tiroir, ce que la caisse sait avant d'avoir tout envoyé, les jetons. La base refait les mêmes calculs et fait foi
// (migration 20261108700000_buvette.sql) ; ici, on affiche juste, même hors ligne.

export type Mode = 'especes' | 'carte' | 'jetons';
export type Category = 'boisson' | 'manger' | 'autre';
export type Validity = 'jour' | 'semaine' | 'mois' | 'saison';

export interface Item { id: string; name: string; emoji: string | null; category: Category; price: number; alcohol: boolean; archived?: boolean }
export interface Permit { id?: string; ref: string; from: string; to: string; note?: string | null; used?: boolean }
export interface OpenSession { id: string; label: string; device: string; by: string; opened_at: string; mine: boolean; fund: number }
export interface CaisseEvent { kind: 'match' | 'event'; id: string; label: string; at: string }
/** La tombola en vente à la caisse (D-183, 20261108800000_tombola.sql). */
export interface CaisseTombola {
  id: string; title: string; price: number; draw_at: string; place: string | null; max: number; sold: number; left: number;
  paper_left: number; phone_left: number; frozen: boolean;
  /** Les numéros réservés à ce téléphone (hors ligne) : papier (le carnet) et téléphone (série T-). */
  paper: number[]; phone: number[]; key: string | null; slug: string | null;
}
export interface CaisseState {
  can: boolean; manager: boolean; minor: boolean; now?: string; me?: string; slot?: string | null;
  tokens?: { on: boolean; name: string }; items?: Item[]; permits?: Permit[]; open?: OpenSession[]; fund_expected?: number;
  events?: CaisseEvent[]; tombola?: CaisseTombola | null;
}
export interface Sums {
  sales: number; bar_sales: number; cash_bar: number; card_bar: number; tok_sales: number; tok_cents: number; tok_short: number;
  cash_tb: number; card_tb: number; tb_sales: number; withdrawn: number;
}
export interface SaleLine { item: string; name: string; emoji?: string | null; unit: number; qty: number; alcohol?: boolean }
export interface SaleRow { id: string; at: string; mode: Mode; total: number; bar: number; tombola: number; void: boolean; short: boolean; lines: SaleLine[] }
export interface SessionInfo {
  id: string; label: string; opened_at: string; by: string; device: string; fund_expected: number; fund_counted: number; exempt: boolean;
  closed_at: string | null; z_no: number | null; sums: Sums; withdrawals: { id: string; at: string; amount: number; keeper: string }[];
  sales: SaleRow[]; pending: number | null; witness: string | null; witness_cents: number | null;
}
/** Une vente de tombola dans une opération de vente (20261108800000) : le nombre, papier ou téléphone, les numéros. */
export interface SaleTombola { tombola: string; n: number; cents: number; kind: 'papier' | 'telephone'; nums: number[]; skipped?: number[]; token?: string }
export type Op =
  | { op: 'sale'; id: string; at: string; mode: Mode; lines: SaleLine[]; code?: string; tombola?: SaleTombola }
  | { op: 'void'; id: string; reason: string; at: string }
  | { op: 'withdraw'; id: string; at: string; amount: number; keeper: string }
  /** « Passer en papier » (D-183) : les tickets téléphone pas encore ouverts d'une vente, remplacés par des billets papier. */
  | { op: 'swap'; id: string; at: string; nums: number[]; skipped: number[] }
  | { op: 'close'; id: string; at: string; count: number; kept: number; note: string; detail: Record<string, number> | null };

export interface ZData {
  no: number; label: string; day: string; opened_at: string; opened_by: string; closed_at: string; closed_by: string;
  bar: { sales: number; especes: number; carte: number; total: number; items: { name: string; qty: number; cents: number }[]; alcohol: boolean; permit: string | null };
  tombola: null | {
    sales: number; especes: number; carte: number; total: number; title?: string; tickets?: number; paper?: number; phone?: number;
    paper_range?: string | null; phone_range?: string | null; skipped?: string[]; frozen_at?: string | null; late?: number;
  };
  jetons: { sales: number; cents: number; short: number; books: boolean; name: string };
  drawer: { fund_expected: number; fund_counted: number; open_gap: number; open_note: string | null; cash_bar: number; cash_tombola: number;
    withdrawals: { at: string; amount: number; keeper: string }[]; withdrawn: number; theoretical: number; counted: number; gap: number; note: string | null;
    detail: Record<string, number> | null; witness: string | null };
  bank: { deposit: number; withdrawn: number; cash: number; card: number; fund_kept: number };
  exempt: boolean; exempt_rank: number | null; counters: Counters;
  compta: Record<string, unknown>;
}
export interface Counters {
  year: number; exempt: number; exempt_max: number; commercial_cents: number; threshold_cents: number; pct: number; alert_pct: number; permits: number; permits_max: number;
}

// --- Les montants ---------------------------------------------------------------------------------------------------------
const nb = (n: number, min = 2) => n.toLocaleString('fr-FR', { minimumFractionDigits: min, maximumFractionDigits: 2 }).replace(/ /g, ' ');
/** « 7,00 € », « 1 200,00 € », « −2,00 € » (le signe moins typographique). */
export const eur = (c: number): string => `${c < 0 ? '−' : ''}${nb(Math.abs(c) / 100)} €`;
/** « 50 € », « 1,50 € » : sans centimes quand il n'y en a pas. */
export const eurRound = (c: number): string => `${c < 0 ? '−' : ''}${nb(Math.abs(c) / 100, Math.abs(c) % 100 ? 2 : 0)} €`;
/** « +2,00 € », « −2,00 € », « 0,00 € ». */
export const eurSigned = (c: number): string => (c > 0 ? `+${eur(c)}` : eur(c));
/** « 7 jetons », « 1 jeton », « 1,5 jeton » (100 = 1 jeton, la valeur d'un euro de la carte, D-152). */
export function jetons(c: number, name = 'jetons'): string {
  const n = c / 100;
  const word = Math.abs(n) < 2 ? singular(name) : name;
  return `${nb(n, Number.isInteger(n) ? 0 : 1)} ${word}`;
}
/** Le nom des jetons au singulier (« jetons » → « jeton », « Paniers » → « Panier », « Jetons du BCL » → « Jeton du BCL »). */
export function singular(name: string): string {
  const [first = '', ...rest] = name.trim().split(/\s+/);
  const one = /[^s]s$/i.test(first) ? first.slice(0, -1) : first.replace(/x$/i, '');
  return [one, ...rest].join(' ');
}
/** « 12,50 », « 12.5 », « 12 € » → 1250 ; sinon null. */
export function parseEuros(s: string): number | null {
  const t = s.replace(/[\s€  ]/g, '').replace(',', '.');
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

// --- La carte et le panier ----------------------------------------------------------------------------------------------
export const CATEGORIES: { key: Category; label: string; tab: string }[] = [
  { key: 'boisson', label: 'Boisson', tab: 'Boissons' }, { key: 'manger', label: 'À manger', tab: 'À manger' }, { key: 'autre', label: 'Autre', tab: 'Autre' },
];
export interface CartLine { item: string; qty: number }
/** L'autorisation temporaire qui couvre un instant (ms), sinon null. */
export function permitAt(permits: Permit[], ms: number): Permit | null {
  return permits.find(p => ms >= Date.parse(p.from) && ms < Date.parse(p.to)) ?? null;
}
/** Ce que la caisse montre : l'alcool seulement pendant une autorisation temporaire (D-152). */
export const sellable = (items: Item[], permits: Permit[], ms: number): Item[] => {
  const ok = !!permitAt(permits, ms);
  return items.filter(i => !i.archived && (!i.alcohol || ok));
};
/** Les lignes d'un panier, au prix de la carte (les articles disparus de la carte sont retirés). */
export function cartLines(cart: CartLine[], items: Item[]): SaleLine[] {
  return cart.flatMap(c => {
    const it = items.find(i => i.id === c.item);
    return it && c.qty > 0 ? [{ item: it.id, name: it.name, emoji: it.emoji, unit: it.price, qty: c.qty, alcohol: it.alcohol }] : [];
  });
}
export const linesTotal = (lines: SaleLine[]): number => lines.reduce((s, l) => s + l.unit * l.qty, 0);
export const linesCount = (lines: SaleLine[]): number => lines.reduce((s, l) => s + l.qty, 0);
export function cartAdd(cart: CartLine[], item: string, d = 1): CartLine[] {
  const out = cart.map(c => ({ ...c }));
  const x = out.find(c => c.item === item);
  if (x) x.qty = Math.max(0, Math.min(99, x.qty + d)); else if (d > 0) out.push({ item, qty: Math.min(99, d) });
  return out.filter(c => c.qty > 0);
}
/** Pourquoi les jetons ne paient pas ce panier (null : ils peuvent). Jamais l'alcool ni la tombola (D-152, D-183). */
export function tokensBlock(lines: SaleLine[], tombola: boolean, on: boolean): string | null {
  if (!on) return 'Le club n’utilise pas les jetons';
  if (lines.some(l => l.alcohol)) return 'Jamais pour l’alcool';
  if (tombola) return 'Jamais pour la tombola';
  return null;
}
/** La ligne sous la carte : les jetons, et l'alcool de ce soir (autorisé jusqu'à…, ou pas d'autorisation). */
export function alcoholLine(items: Item[], permits: Permit[], ms: number, tz = 'Europe/Paris'): string | null {
  const alc = items.filter(i => i.alcohol && !i.archived);
  if (!alc.length) return null;
  const p = permitAt(permits, ms);
  const what = alc[0]!.name.toLowerCase();
  if (!p) return `Pas de ${what} ce soir : aucune autorisation du maire pour ce match.`;
  const until = new Date(p.to).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: tz }).replace(':', ' h ');
  return `Alcool autorisé jusqu’à ${until} (${p.ref}) : jamais pour un mineur, jamais en jetons.`;
}

// --- Le tiroir ----------------------------------------------------------------------------------------------------------
/** Les billets et pièces, en centimes (le comptage par coupures). */
export const DENOMS = [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1];
export const denomLabel = (c: number): string => (c >= 500 ? `${c / 100} €` : c >= 100 ? `${c / 100} €` : `${c} c`);
export const countOf = (detail: Record<string, number>): number => Object.entries(detail).reduce((s, [k, n]) => s + Number(k) * (Number(n) || 0), 0);
export const ZERO_SUMS: Sums = { sales: 0, bar_sales: 0, cash_bar: 0, card_bar: 0, tok_sales: 0, tok_cents: 0, tok_short: 0, cash_tb: 0, card_tb: 0, tb_sales: 0, withdrawn: 0 };

/** Ce que la caisse sait : ce que le serveur a reçu, plus la file pas encore envoyée (ventes, annulations, prélèvements). */
export function localSums(server: Sums | null, queue: Op[], known: SaleRow[] = []): Sums {
  const s: Sums = { ...(server ?? ZERO_SUMS) };
  const sales = new Map<string, { mode: Mode; bar: number; tb: number }>();
  for (const k of known) if (!k.void) sales.set(k.id, { mode: k.mode, bar: k.bar, tb: k.tombola });
  const add = (mode: Mode, bar: number, tb: number, sign: 1 | -1) => {
    s.sales += sign;
    if (mode === 'especes') { s.cash_bar += sign * bar; s.cash_tb += sign * tb; }
    else if (mode === 'carte') { s.card_bar += sign * bar; s.card_tb += sign * tb; }
    else { s.tok_cents += sign * bar; s.tok_sales += sign; }
    if (bar > 0 && mode !== 'jetons') s.bar_sales += sign;
    if (tb > 0) s.tb_sales += sign;
  };
  for (const o of queue) {
    if (o.op === 'sale') {
      const bar = linesTotal(o.lines), tb = o.tombola?.cents ?? 0;
      sales.set(o.id, { mode: o.mode, bar, tb });
      add(o.mode, bar, tb, 1);
    } else if (o.op === 'void') {
      const x = sales.get(o.id);
      if (x) { add(x.mode, x.bar, x.tb, -1); sales.delete(o.id); }
    } else if (o.op === 'withdraw') s.withdrawn += o.amount;
  }
  return s;
}
/** Les espèces attendues dans le tiroir : le fond compté à l'ouverture, les ventes en espèces, moins les prélèvements. */
export const theoretical = (fundCounted: number, s: Sums): number => fundCounted + s.cash_bar + s.cash_tb - s.withdrawn;
/** « Écart nul », « Il manque 2,00 € », « 2,00 € de trop ». */
export function gapText(gap: number): string {
  if (gap === 0) return 'Écart nul';
  return gap < 0 ? `Il manque ${eur(-gap)}` : `${eur(gap)} de trop`;
}

// --- Les compteurs (D-184) ----------------------------------------------------------------------------------------------
/** « celle-ci est la 2e sur 6 de l'année » ; au-delà de 6, ses recettes comptent dans la franchise (indiquer, ne pas interdire). */
export function exemptLine(rank: number | null, max = 6): string | null {
  if (!rank) return null;
  if (rank > max) return `celle-ci est la ${rank}e de l’année : au-delà de ${max}, ses recettes comptent dans la franchise.`;
  return `celle-ci est la ${rank === 1 ? '1re' : `${rank}e`} sur ${max} de l’année.`;
}
/** « 8 % du seuil ; Arceau prévient à 75 % » ; à 75 % et plus, l'alerte. */
export function franchiseLine(c: Counters): { text: string; alert: boolean } {
  const pct = Math.round(c.pct);
  const alert = c.pct >= c.alert_pct;
  return { alert, text: alert
    ? `${pct} % du seuil de ${eurRound(c.threshold_cents)} (${eurRound(c.commercial_cents)}) : au-delà, impôts commerciaux et logiciel de caisse certifié. Parles-en au trésorier.`
    : `${pct} % du seuil de ${eurRound(c.threshold_cents)} ; Arceau prévient à ${c.alert_pct} %.` };
}

// --- Les jetons ---------------------------------------------------------------------------------------------------------
export const VALIDITY: Record<Validity, string> = { jour: 'le jour même', semaine: 'une semaine', mois: 'un mois', saison: 'jusqu’au 30 juin' };
export const TOKEN_KIND: Record<string, string> = { benevolat: 'Bénévolat', don: 'Contrepartie d’un don', partenaire: 'Partenaire apporté', geste: 'Un merci du club' };
/** Ce que la base accorde pour un don (D-152) : le plus petit du pourcentage du club, de 25 % du don et de 65 € moins les
 *  contreparties de l'année ; par demi-jeton. */
export function giftCap(donation: number, pct: number, already: number): number {
  const v = Math.min(Math.floor(donation * pct / 100), Math.floor(donation / 4), 6500 - already);
  return Math.max(0, Math.floor(v / 50) * 50);
}

// --- Divers -------------------------------------------------------------------------------------------------------------
/** Une raison de refus de la base, lisible (« refused:Alcool sans autorisation… » → « Alcool sans autorisation… »). */
export const refusal = (st: string): string => st.replace(/^refused:/, '').replace(/^ERROR:\s*/, '').trim();
/** « 21:04 » à l'heure du club. */
export const hhmm = (iso: string | number, tz = 'Europe/Paris'): string => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
/** « 22 h 52 ». */
export const hHmm = (iso: string | number, tz = 'Europe/Paris'): string => hhmm(iso, tz).replace(':', ' h ');
export const newId = (): string => (crypto.randomUUID ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));
